import { Box, SelectChangeEvent, Stack, Typography } from '@mui/material';
import NumberField from './NumberField';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import {
  clampThresholdMeters,
  DEFAULT_THRESHOLD_M,
  formatMetersDisplay,
  isUsableThresholdMeters,
  THRESHOLD_M_DECIMAL_PLACES,
  THRESHOLD_M_MAX,
  THRESHOLD_M_MIN,
  THRESHOLD_M_STEP,
} from './distanceBasedSensorCheckUtils';

export interface DistanceBasedSensorData {
  port: number;
  distance: number;
  objectPresent: boolean;
  distanceThreshold?: number;
}

export type DistanceBasedSensorType = 'beamBreak' | 'distance';

export interface UseDistanceBasedSensorCheckConfig {
  sensorType: DistanceBasedSensorType;
  port: number | undefined;
  open: boolean;
  disableWrites?: boolean;
  detectionRanges: readonly { label: string; valueInMeters: number }[];
  defaultThresholdMeters?: number;
  /** Explicit threshold to display and write once when this check opens. */
  initialThresholdMeters?: number;
  thresholdDecimalPlaces?: number;
  allowZeroThreshold?: boolean;
  pollSensor: (port: number) => void;
  setSensorSettings: (port: number, thresholdInMeters: number) => void;
}

export function useDistanceBasedSensorCheck({
  sensorType,
  port,
  open,
  disableWrites = false,
  detectionRanges,
  defaultThresholdMeters,
  initialThresholdMeters,
  thresholdDecimalPlaces = THRESHOLD_M_DECIMAL_PLACES,
  allowZeroThreshold = false,
  pollSensor,
  setSensorSettings,
}: UseDistanceBasedSensorCheckConfig) {
  const [detectionRange, setDetectionRange] = useState<string>(detectionRanges[0]?.label ?? '');
  const fallbackThresholdMeters = clampThresholdMeters(defaultThresholdMeters ?? DEFAULT_THRESHOLD_M, thresholdDecimalPlaces);
  const configuredInitialThresholdMeters =
    initialThresholdMeters == null ? null : clampThresholdMeters(initialThresholdMeters, thresholdDecimalPlaces);
  const [thresholdMeters, setThresholdMetersState] = useState<number>(configuredInitialThresholdMeters ?? fallbackThresholdMeters);
  const hasSyncedFromSensorThisOpen = useRef(false);
  const pendingThresholdWriteRef = useRef<{ port: number; thresholdMeters: number } | null>(null);

  const sensorData = useAppSelector((state) => {
    if (port == null) return null;
    if (sensorType === 'beamBreak') {
      return state.robotData.beamBreakSensorData[port] ?? null;
    }
    return state.robotData.distanceSensorData[port] ?? null;
  }) as DistanceBasedSensorData | null;

  const distance = sensorData?.distance ?? 0;
  const distanceThreshold = sensorData?.distanceThreshold ?? 0;
  const beamBroken = sensorData?.objectPresent ?? false;

  // When dialog closes, reset so we sync from sensor again on next open
  useEffect(() => {
    if (!open) {
      hasSyncedFromSensorThisOpen.current = false;
      pendingThresholdWriteRef.current = null;
    }
  }, [open]);

  // Initialize the threshold once per open. Config-launched checks own their initial
  // threshold; Electronic Explorer checks continue to sync from the sensor response.
  useEffect(() => {
    if (!open || port == null || hasSyncedFromSensorThisOpen.current) return;

    if (configuredInitialThresholdMeters != null) {
      setThresholdMetersState(configuredInitialThresholdMeters);
      hasSyncedFromSensorThisOpen.current = true;
      if (disableWrites) {
        pendingThresholdWriteRef.current = { port, thresholdMeters: configuredInitialThresholdMeters };
      } else {
        pendingThresholdWriteRef.current = null;
        setSensorSettings(port, configuredInitialThresholdMeters);
      }
      return;
    }

    if (sensorData == null) return;

    const sensorThreshold = sensorData.distanceThreshold;
    const shouldConfigureDefault = defaultThresholdMeters != null && !isUsableThresholdMeters(sensorThreshold, { allowZero: allowZeroThreshold });

    let nextThreshold: number;
    if (shouldConfigureDefault) {
      nextThreshold = fallbackThresholdMeters;
    } else {
      if (sensorThreshold == null) return;
      nextThreshold = clampThresholdMeters(sensorThreshold, thresholdDecimalPlaces);
    }

    setThresholdMetersState(nextThreshold);
    hasSyncedFromSensorThisOpen.current = true;

    if (shouldConfigureDefault) {
      if (disableWrites) {
        pendingThresholdWriteRef.current = { port, thresholdMeters: nextThreshold };
      } else {
        pendingThresholdWriteRef.current = null;
        setSensorSettings(port, nextThreshold);
      }
    }
  }, [
    allowZeroThreshold,
    configuredInitialThresholdMeters,
    defaultThresholdMeters,
    disableWrites,
    fallbackThresholdMeters,
    open,
    port,
    sensorData,
    setSensorSettings,
    thresholdDecimalPlaces,
  ]);

  useEffect(() => {
    if (!open || disableWrites || port == null) return;
    const pendingThresholdWrite = pendingThresholdWriteRef.current;
    if (pendingThresholdWrite == null || pendingThresholdWrite.port !== port) return;

    pendingThresholdWriteRef.current = null;
    setSensorSettings(port, pendingThresholdWrite.thresholdMeters);
  }, [disableWrites, open, port, setSensorSettings]);

  const setThresholdMeters = (meters: number) => {
    const clamped = clampThresholdMeters(meters, thresholdDecimalPlaces);
    setThresholdMetersState(clamped);
    if (port != null && !disableWrites) {
      pendingThresholdWriteRef.current = null;
      setSensorSettings(port, clamped);
    }
  };

  // Poll sensor at a faster rate when dialog is open
  useEffect(() => {
    if (!open || port == null) return;
    pollSensor(port);
    const id = setInterval(() => pollSensor(port), 100);
    return () => clearInterval(id);
  }, [open, port, pollSensor]);

  const handleDetectionRangeChange = (event: SelectChangeEvent<string>) => {
    const label = event.target.value;
    setDetectionRange(label);
    if (port != null && !disableWrites) {
      const range = detectionRanges.find((r) => r.label === label);
      if (range != null) {
        setSensorSettings(port, range.valueInMeters);
      }
    }
  };

  return {
    sensorData,
    beamBroken,
    distance,
    distanceThreshold,
    detectionRange,
    handleDetectionRangeChange,
    detectionRanges,
    thresholdMeters,
    setThresholdMeters,
  };
}

export interface DistanceBasedSensorCheckContentProps {
  beamBroken: boolean;
  distance: number;
  /** Threshold in meters for numeric input (0 to 1). */
  thresholdMeters: number;
  /** Update display and write to sensor. */
  onThresholdMetersChange: (meters: number) => void;
  /** Finalize the current threshold value. */
  onThresholdMetersCommitted: (meters: number) => void;
  disabled?: boolean;
  /** Show threshold controls. */
  showThreshold?: boolean;
  /** Show the "Object Detected" section. */
  showObjectDetected?: boolean;
  thresholdDecimalPlaces?: number;
  brokenTrueI18nKey: string;
  notBrokenFalseI18nKey: string;
}

const LABEL_WIDTH = 150;
const BOX_WIDTH = 120;
const BOX_HEIGHT = 40;
/** Slightly wider than BOX_WIDTH so helper text can wrap to 2 lines. */
const NUMBER_FIELD_HELPER_WIDTH = 150;

const valueBoxSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  color: 'text.primary',
  width: BOX_WIDTH,
  height: BOX_HEIGHT,
  py: 0,
  px: 2,
  textAlign: 'center' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box' as const,
};

export function DistanceBasedSensorCheckContent({
  beamBroken,
  distance,
  thresholdMeters,
  onThresholdMetersChange,
  onThresholdMetersCommitted,
  disabled = false,
  showThreshold = true,
  showObjectDetected = true,
  thresholdDecimalPlaces = THRESHOLD_M_DECIMAL_PLACES,
  brokenTrueI18nKey,
  notBrokenFalseI18nKey,
}: DistanceBasedSensorCheckContentProps) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const distanceDisplay = formatMetersDisplay(distance, locale);

  const labelSx = { width: LABEL_WIDTH, flexShrink: 0 };

  return (
    <Stack spacing={1.5} alignItems="stretch" sx={{ width: '100%', maxWidth: 400 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body1" color="text.primary" sx={labelSx}>
          <Trans i18nKey="distanceBasedSensorCheck.currentDistance">Current Distance:</Trans>
        </Typography>
        <Box sx={valueBoxSx}>
          <Typography variant="h6" component="span" fontWeight="bold">
            {distanceDisplay}
          </Typography>
        </Box>
      </Stack>

      {showThreshold && (
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ '& .MuiFormControl-root': { flexShrink: 0 } }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                ...labelSx,
                lineHeight: `${BOX_HEIGHT}px`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Trans i18nKey="distanceBasedSensorCheck.detectionThreshold">Detection Threshold:</Trans>
            </Typography>
            <NumberField
              min={THRESHOLD_M_MIN}
              max={THRESHOLD_M_MAX}
              step={THRESHOLD_M_STEP}
              format={{ minimumFractionDigits: THRESHOLD_M_DECIMAL_PLACES, maximumFractionDigits: thresholdDecimalPlaces }}
              locale={locale}
              size="small"
              disabled={disabled}
              value={thresholdMeters}
              onValueChange={(newValue) => {
                if (newValue != null) {
                  onThresholdMetersChange(clampThresholdMeters(newValue, thresholdDecimalPlaces));
                } else {
                  onThresholdMetersChange(THRESHOLD_M_MIN);
                }
              }}
              onValueCommitted={(newValue) => {
                if (newValue != null) {
                  onThresholdMetersCommitted(clampThresholdMeters(newValue, thresholdDecimalPlaces));
                } else {
                  onThresholdMetersCommitted(THRESHOLD_M_MIN);
                }
              }}
              helperText={<Trans i18nKey="distanceBasedSensorCheck.thresholdHelper">Enter a value between 0 m and 1 m</Trans>}
              sx={{
                width: BOX_WIDTH,
                '& .MuiOutlinedInput-root': {
                  height: BOX_HEIGHT,
                  minHeight: BOX_HEIGHT,
                },
                '& .MuiFormHelperText-root': {
                  width: NUMBER_FIELD_HELPER_WIDTH,
                  maxWidth: NUMBER_FIELD_HELPER_WIDTH,
                },
              }}
            />
          </Stack>
        </Stack>
      )}

      {showObjectDetected && (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body1" color="text.primary" sx={labelSx}>
            <Trans i18nKey="distanceBasedSensorCheck.objectDetected">Object Detected:</Trans>
          </Typography>
          <Box
            sx={{
              ...valueBoxSx,
              bgcolor: beamBroken ? 'success.main' : 'action.selected',
              color: beamBroken ? 'success.contrastText' : 'text.secondary',
            }}
          >
            <Typography variant="h6" component="span" fontWeight="bold">
              {beamBroken ? <Trans i18nKey={brokenTrueI18nKey}>YES</Trans> : <Trans i18nKey={notBrokenFalseI18nKey}>NO</Trans>}
            </Typography>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
